import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, useLayoutEffect } from "react";
import { syncOverlayHistory } from "./lib/overlayHistory";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import OAuthConsent from "./pages/OAuthConsent";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Pipeline from "./pages/Pipeline";
import ProspectFormPage from "./pages/ProspectFormPage";
import Prospects from "./pages/Prospects";
import ProspectDetail from "./pages/ProspectDetail";
import Pilots from "./pages/Pilots";
import PilotWorkspace from "./pages/PilotWorkspace";
import Tasks from "./pages/Tasks";
import Dashboard from "./pages/Dashboard";
import CSVImport from "./pages/CSVImport";
import NotFound from "./pages/NotFound";
import GoogleSheet from "./pages/GoogleSheet";
import Profile from "./pages/Profile";
import Apps from "./pages/Apps";
import CopilotPage from "./pages/CopilotPage";
import Users from "./pages/Users";
import UserFormPage from "./pages/UserFormPage";
import Roles from "./pages/Roles";
import Activity from "./pages/Activity";
import Trash from "./pages/Trash";
import WhatsApp from "./pages/WhatsApp";
import Collaborators from "./pages/Collaborators";
import CollaboratorFormPage from "./pages/CollaboratorFormPage";
import CollaboratorImportPage from "./pages/CollaboratorImportPage";
import CollaboratorDetail from "./pages/CollaboratorDetail";
import Organisation from "./pages/Organisation";
// The organisation chart is its own chunk (React Flow, dagre, the page): it never weighs on the main bundle.
const OrgChart = lazy(() => import("./pages/OrgChart"));
import Missions from "./pages/Missions";
import Customers from "./pages/Customers";
import CustomerPage from "./pages/CustomerPage";
import { CUSTOMER_READ_KEYS } from "./lib/customers";
import { TICKET_CREATE_KEYS, TICKET_READ_KEYS } from "./lib/tickets";
import Tickets from "./pages/Tickets";
import TicketFormPage from "./pages/TicketFormPage";
import TicketDetail from "./pages/TicketDetail";
import SettingsTickets from "./pages/SettingsTickets";
import SettingsAutomation from "./pages/SettingsAutomation";
import SettingsAgents from "./pages/SettingsAgents";
import { PRODUCT_CREATE_KEYS, PRODUCT_READ_KEYS } from "./lib/product";
import ProductItems from "./pages/ProductItems";
import ProductItemFormPage from "./pages/ProductItemFormPage";
import ProductItemDetail from "./pages/ProductItemDetail";
import ProductReleases from "./pages/ProductReleases";
import ProductIncidents from "./pages/ProductIncidents";
import ProductIncidentFormPage from "./pages/ProductIncidentFormPage";
import ProductIncidentDetail from "./pages/ProductIncidentDetail";
import ProductReleaseDetail from "./pages/ProductReleaseDetail";
import SettingsBilling from "./pages/SettingsBilling";
import SettingsDeletion from "./pages/SettingsDeletion";
import SettingsGeneral from "./pages/SettingsGeneral";
import BillingOffers from "./pages/BillingOffers";
import BillingOfferEditor from "./pages/BillingOfferEditor";
import BillingSubscriptions from "./pages/BillingSubscriptions";
import Records from "./pages/Records";
import RecordFormPage from "./pages/RecordFormPage";
import RecordDocument from "./pages/RecordDocument";
import RecordObligations from "./pages/RecordObligations";
import RecordAllFiles from "./pages/RecordAllFiles";
import RecordConstitution from "./pages/RecordConstitution";
import RecordTemplates from "./pages/RecordTemplates";
import RecordTemplateEditor from "./pages/RecordTemplateEditor";
import RecordBrand from "./pages/RecordBrand";
import RecordClauses from "./pages/RecordClauses";
import RecordClauseEditor from "./pages/RecordClauseEditor";
import RecordGenerate from "./pages/RecordGenerate";
import SettingsRecords from "./pages/SettingsRecords";
import SettingsProcurement from "./pages/SettingsProcurement";
import ProcurementInventory from "./pages/ProcurementInventory";
import ProcurementInventoryDetail from "./pages/ProcurementInventoryDetail";
import BillingSubscriptionForm from "./pages/BillingSubscriptionForm";
import BillingSubscription from "./pages/BillingSubscription";
import BillingInvoices from "./pages/BillingInvoices";
import ProcurementAssets from "./pages/ProcurementAssets";
import ProcurementAssetForm from "./pages/ProcurementAssetForm";
import ProcurementAsset from "./pages/ProcurementAsset";
import ProcurementSuppliers from "./pages/ProcurementSuppliers";
import ProcurementMine from "./pages/ProcurementMine";
import ProcurementRequests from "./pages/ProcurementRequests";
import ProcurementRequestForm from "./pages/ProcurementRequestForm";
import ProcurementRequest from "./pages/ProcurementRequest";
import MarketingCampaigns from "./pages/MarketingCampaigns";
import MarketingPartners from "./pages/MarketingPartners";
import MarketingIndicators from "./pages/MarketingIndicators";
import Cockpit from "./pages/Cockpit";
import Architecture from "./pages/Architecture";
import AdminShell from "./components/jamora-admin/AdminShell";
import JamoraAdminOverview from "./pages/JamoraAdminOverview";
import { canViewPlatform as canViewJamoraPlatform, homePath as jamoraAdminHome } from "./lib/jamoraAdmin";
import { useAuth } from "./contexts/AuthContext";
import JamoraAdminPharmacies from "./pages/JamoraAdminPharmacies";
import JamoraAdminPharmacy from "./pages/JamoraAdminPharmacy";
import JamoraAdminPharmacyChoice from "./pages/JamoraAdminPharmacyChoice";
import JamoraAdminDemoRequests from "./pages/JamoraAdminDemoRequests";
import JamoraAdminPrinting from "./pages/JamoraAdminPrinting";
import JamoraAdminOnepharma from "./pages/JamoraAdminOnepharma";
import JamoraAdminUsers from "./pages/JamoraAdminUsers";
import JamoraAdminCatalogues from "./pages/JamoraAdminCatalogues";
import JamoraAdminLogs from "./pages/JamoraAdminLogs";
import GovernanceDecisions from "./pages/GovernanceDecisions";
import GovernanceDecisionForm from "./pages/GovernanceDecisionForm";
import GovernanceDecision from "./pages/GovernanceDecision";
import GovernanceMeetings from "./pages/GovernanceMeetings";
import GovernanceMeetingForm from "./pages/GovernanceMeetingForm";
import GovernanceMeeting from "./pages/GovernanceMeeting";
import GovernanceRisks from "./pages/GovernanceRisks";
import GovernanceRiskForm from "./pages/GovernanceRiskForm";
import GovernanceRisk from "./pages/GovernanceRisk";
import KnowledgeArticles from "./pages/KnowledgeArticles";
import KnowledgeArticle from "./pages/KnowledgeArticle";
import KnowledgeArticleEditor from "./pages/KnowledgeArticleEditor";
import Equipment from "./pages/Equipment";
import { EQUIPMENT_READ_KEYS } from "./lib/equipment";
import { MISSION_READ_KEYS, MISSION_WRITE_KEYS } from "./lib/missions";
import MissionFormPage from "./pages/MissionFormPage";
import MissionDetail from "./pages/MissionDetail";
import Procedures from "./pages/Procedures";
import ProcedureEditor from "./pages/ProcedureEditor";
import MissionReport from "./pages/MissionReport";
import ChecklistRun from "./pages/ChecklistRun";
import ClaimFormPage from "./pages/ClaimFormPage";
import Finance from "./pages/Finance";
import FinanceExpenseFormPage from "./pages/FinanceExpenseFormPage";
import FinanceImportPage from "./pages/FinanceImportPage";
import Assistance from "./pages/Assistance";
import AssistSession from "./pages/AssistSession";
import { ASSIST_READ, SETTINGS_ACCESS, TRASH_ACCESS, WHATSAPP_ACCESS } from "./lib/permissions";
import Settings, { SettingsIndex, SettingsTasks, SettingsNotifications, SettingsSystem } from "./pages/Settings";
import SettingsAssistant from "./pages/SettingsAssistant";
import SettingsAssist from "./pages/SettingsAssist";
import SettingsAssistantUsage from "./pages/SettingsAssistantUsage";
import SettingsTrainingCatalog from "./pages/SettingsTrainingCatalog";
import Trainings from "./pages/Trainings";
import TrainingModuleForm from "./pages/TrainingModuleForm";
import TrainingProgramEditor from "./pages/TrainingProgramEditor";
import TrainingSession from "./pages/TrainingSession";
import TrainingSessionFormPage from "./pages/TrainingSessionFormPage";
import { CATALOG_READ_KEYS, SESSION_CREATE_KEYS, TRAINING_KEYS, TRAINING_MANAGE_CATALOG } from "./lib/training";
import { Toaster } from "sonner";

// The application's front door: the overview for whoever reads the platform, the choice of a pharmacy for whoever may only open files.
function JamoraAdminHome() {
  const { user } = useAuth();
  return canViewJamoraPlatform(user) ? <JamoraAdminOverview /> : <Navigate to={jamoraAdminHome(user)} replace />;
}

function OverlayHistorySync() {
  const location = useLocation();
  useLayoutEffect(syncOverlayHistory, [location]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <OverlayHistorySync />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/oauth/consent" element={<ProtectedRoute feature="agent_access"><OAuthConsent /></ProtectedRoute>} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route
              path="pipeline"
              element={
                <ProtectedRoute permission="prospects:view">
                  <Pipeline />
                </ProtectedRoute>
              }
            />
            <Route
              path="prospects"
              element={
                <ProtectedRoute permission="prospects:view">
                  <Prospects />
                </ProtectedRoute>
              }
            />
            <Route
              path="prospects/nouveau"
              element={
                <ProtectedRoute permission="prospects:create">
                  <ProspectFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="prospects/:id"
              element={
                <ProtectedRoute permission="prospects:view">
                  <ProspectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="tasks"
              element={
                <ProtectedRoute permission="tasks:view">
                  <Tasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="pilotes"
              element={
                <ProtectedRoute permission="prospects:view">
                  <Pilots />
                </ProtectedRoute>
              }
            />
            <Route
              path="pilotes/:prospectId"
              element={
                <ProtectedRoute permission="prospects:view">
                  <PilotWorkspace />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients"
              element={
                <ProtectedRoute permission={CUSTOMER_READ_KEYS} feature="customers">
                  <Customers />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients/:prospectId"
              element={
                <ProtectedRoute permission={CUSTOMER_READ_KEYS} feature="customers">
                  <CustomerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="produit"
              element={<ProtectedRoute permission={PRODUCT_READ_KEYS} feature="product"><ProductItems /></ProtectedRoute>}
            />
            <Route
              path="produit/nouveau"
              element={<ProtectedRoute permission={PRODUCT_CREATE_KEYS} feature="product"><ProductItemFormPage /></ProtectedRoute>}
            />
            <Route
              path="produit/versions"
              element={<ProtectedRoute permission={PRODUCT_READ_KEYS} feature="product"><ProductReleases /></ProtectedRoute>}
            />
            <Route
              path="produit/versions/:releaseId"
              element={<ProtectedRoute permission={PRODUCT_READ_KEYS} feature="product"><ProductReleaseDetail /></ProtectedRoute>}
            />
            <Route
              path="produit/incidents"
              element={<ProtectedRoute permission={PRODUCT_READ_KEYS} feature="product"><ProductIncidents /></ProtectedRoute>}
            />
            <Route
              path="produit/incidents/nouveau"
              element={<ProtectedRoute permission={["product:manage_incidents"]} feature="product"><ProductIncidentFormPage /></ProtectedRoute>}
            />
            <Route
              path="produit/incidents/:incidentId"
              element={<ProtectedRoute permission={PRODUCT_READ_KEYS} feature="product"><ProductIncidentDetail /></ProtectedRoute>}
            />
            <Route
              path="produit/:itemId"
              element={<ProtectedRoute permission={PRODUCT_READ_KEYS} feature="product"><ProductItemDetail /></ProtectedRoute>}
            />
            <Route
              path="support/tickets"
              element={<ProtectedRoute permission={TICKET_READ_KEYS} feature="tickets"><Tickets /></ProtectedRoute>}
            />
            <Route
              path="support/tickets/nouveau"
              element={<ProtectedRoute permission={TICKET_CREATE_KEYS} feature="tickets"><TicketFormPage /></ProtectedRoute>}
            />
            <Route
              path="support/tickets/:ticketId"
              element={<ProtectedRoute permission={TICKET_READ_KEYS} feature="tickets"><TicketDetail /></ProtectedRoute>}
            />
            {/* Revenus (Ordre 17): every screen reads with billing:view; each write key is checked by the screen and the server. */}
            <Route path="revenus/offres" element={<ProtectedRoute permission="billing:view" feature="billing"><BillingOffers /></ProtectedRoute>} />
            <Route path="revenus/offres/:offerId" element={<ProtectedRoute permission="billing:view" feature="billing"><BillingOfferEditor /></ProtectedRoute>} />
            <Route path="revenus/abonnements" element={<ProtectedRoute permission="billing:view" feature="billing"><BillingSubscriptions /></ProtectedRoute>} />
            <Route
              path="revenus/abonnements/nouveau"
              element={<ProtectedRoute permission="billing:manage_subscriptions" feature="billing"><BillingSubscriptionForm /></ProtectedRoute>}
            />
            <Route path="revenus/abonnements/:subscriptionId" element={<ProtectedRoute permission="billing:view" feature="billing"><BillingSubscription /></ProtectedRoute>} />
            <Route path="revenus/factures" element={<ProtectedRoute permission="billing:view" feature="billing"><BillingInvoices /></ProtectedRoute>} />
            {/* Achats et actifs (Ordre 20): the inventory, its creation form, one asset, the suppliers, and « Mon matériel » (holder relation, D-62). */}
            <Route path="achats/demandes" element={<ProtectedRoute permission={["procurement:create_request", "procurement:view"]} feature="procurement"><ProcurementRequests /></ProtectedRoute>} />
            <Route path="achats/demandes/nouvelle" element={<ProtectedRoute permission="procurement:create_request" feature="procurement"><ProcurementRequestForm /></ProtectedRoute>} />
            <Route path="achats/demandes/:requestId" element={<ProtectedRoute permission={["procurement:create_request", "procurement:view"]} feature="procurement"><ProcurementRequest /></ProtectedRoute>} />
            <Route path="achats/demandes/:requestId/modifier" element={<ProtectedRoute permission="procurement:create_request" feature="procurement"><ProcurementRequestForm /></ProtectedRoute>} />
            <Route path="achats/actifs" element={<ProtectedRoute permission="assets:view" feature="procurement"><ProcurementAssets /></ProtectedRoute>} />
            <Route path="achats/actifs/nouveau" element={<ProtectedRoute permission="assets:manage" feature="procurement"><ProcurementAssetForm /></ProtectedRoute>} />
            <Route path="achats/actifs/:assetId" element={<ProtectedRoute permission={["assets:view", "assets:view_own"]} feature="procurement"><ProcurementAsset /></ProtectedRoute>} />
            <Route path="achats/inventaire" element={<ProtectedRoute permission="assets:view" feature="procurement"><ProcurementInventory /></ProtectedRoute>} />
            <Route path="achats/inventaire/:inventoryId" element={<ProtectedRoute permission="assets:view" feature="procurement"><ProcurementInventoryDetail /></ProtectedRoute>} />
            <Route path="achats/fournisseurs" element={<ProtectedRoute permission="procurement:view" feature="procurement"><ProcurementSuppliers /></ProtectedRoute>} />
            <Route path="marketing/campagnes" element={<ProtectedRoute permission="marketing:view" feature="marketing"><MarketingCampaigns /></ProtectedRoute>} />
            <Route path="marketing/partenaires" element={<ProtectedRoute permission="marketing:view" feature="marketing"><MarketingPartners /></ProtectedRoute>} />
            <Route path="marketing/indicateurs" element={<ProtectedRoute permission="marketing:view" feature="marketing"><MarketingIndicators /></ProtectedRoute>} />
            <Route path="cockpit" element={<ProtectedRoute permission="analytics:view_company" feature="cockpit"><Cockpit /></ProtectedRoute>} />
            <Route path="architecture" element={<ProtectedRoute permission="architecture:view" feature="architecture"><Architecture /></ProtectedRoute>} />
            {/* Administration Jamora (Ordre 27): one shell (re-authentication, context selector, banner), screens by context. */}
            <Route path="jamora-admin" element={<ProtectedRoute permission="jamora_admin:access" feature="jamora_admin"><AdminShell /></ProtectedRoute>}>
              <Route index element={<ProtectedRoute permission={["jamora_admin:view_platform", "jamora_admin:view_pharmacy", "jamora_admin:view_logs"]} feature="jamora_admin"><JamoraAdminHome /></ProtectedRoute>} />
              <Route path="pharmacies" element={<ProtectedRoute permission="jamora_admin:view_platform" feature="jamora_admin"><JamoraAdminPharmacies /></ProtectedRoute>} />
              <Route path="demandes-de-demo" element={<ProtectedRoute permission="jamora_admin:view_platform" feature="jamora_admin"><JamoraAdminDemoRequests /></ProtectedRoute>} />
              <Route path="impression" element={<ProtectedRoute permission="jamora_admin:view_platform" feature="jamora_admin"><JamoraAdminPrinting /></ProtectedRoute>} />
              <Route path="onepharma" element={<ProtectedRoute permission="jamora_admin:view_platform" feature="jamora_admin"><JamoraAdminOnepharma /></ProtectedRoute>} />
              <Route path="catalogues" element={<ProtectedRoute permission="jamora_admin:view_platform" feature="jamora_admin"><JamoraAdminCatalogues /></ProtectedRoute>} />
              <Route path="comptes" element={<ProtectedRoute permission="jamora_admin:view_platform" feature="jamora_admin"><JamoraAdminUsers /></ProtectedRoute>} />
              <Route path="journaux" element={<ProtectedRoute permission="jamora_admin:view_logs" feature="jamora_admin"><JamoraAdminLogs /></ProtectedRoute>} />
              <Route path="p" element={<ProtectedRoute permission={["jamora_admin:view_platform", "jamora_admin:view_pharmacy"]} feature="jamora_admin"><JamoraAdminPharmacyChoice /></ProtectedRoute>} />
              <Route path="p/:schema" element={<ProtectedRoute permission={["jamora_admin:view_platform", "jamora_admin:view_pharmacy"]} feature="jamora_admin"><JamoraAdminPharmacy /></ProtectedRoute>} />
            </Route>
            <Route path="gouvernance/decisions" element={<ProtectedRoute permission="governance:view" feature="governance"><GovernanceDecisions /></ProtectedRoute>} />
            <Route path="gouvernance/decisions/nouvelle" element={<ProtectedRoute permission="governance:create_decision" feature="governance"><GovernanceDecisionForm /></ProtectedRoute>} />
            <Route path="gouvernance/decisions/:decisionId" element={<ProtectedRoute permission="governance:view" feature="governance"><GovernanceDecision /></ProtectedRoute>} />
            <Route path="gouvernance/decisions/:decisionId/modifier" element={<ProtectedRoute permission="governance:create_decision" feature="governance"><GovernanceDecisionForm /></ProtectedRoute>} />
            <Route path="gouvernance/reunions" element={<ProtectedRoute permission="governance:view" feature="governance"><GovernanceMeetings /></ProtectedRoute>} />
            <Route path="gouvernance/reunions/nouvelle" element={<ProtectedRoute permission="governance:manage_meetings" feature="governance"><GovernanceMeetingForm /></ProtectedRoute>} />
            <Route path="gouvernance/reunions/:meetingId" element={<ProtectedRoute permission="governance:view" feature="governance"><GovernanceMeeting /></ProtectedRoute>} />
            <Route path="gouvernance/reunions/:meetingId/modifier" element={<ProtectedRoute permission="governance:manage_meetings" feature="governance"><GovernanceMeetingForm /></ProtectedRoute>} />
            <Route path="gouvernance/risques" element={<ProtectedRoute permission="governance:view" feature="governance"><GovernanceRisks /></ProtectedRoute>} />
            <Route path="gouvernance/risques/nouveau" element={<ProtectedRoute permission="governance:manage_risks" feature="governance"><GovernanceRiskForm /></ProtectedRoute>} />
            <Route path="gouvernance/risques/:riskId" element={<ProtectedRoute permission="governance:view" feature="governance"><GovernanceRisk /></ProtectedRoute>} />
            <Route path="gouvernance/risques/:riskId/modifier" element={<ProtectedRoute permission="governance:manage_risks" feature="governance"><GovernanceRiskForm /></ProtectedRoute>} />
            <Route path="support/connaissances" element={<ProtectedRoute permission="knowledge:view" feature="governance"><KnowledgeArticles /></ProtectedRoute>} />
            <Route path="support/connaissances/nouveau" element={<ProtectedRoute permission="knowledge:manage" feature="governance"><KnowledgeArticleEditor /></ProtectedRoute>} />
            <Route path="support/connaissances/:articleId" element={<ProtectedRoute permission="knowledge:view" feature="governance"><KnowledgeArticle /></ProtectedRoute>} />
            <Route path="support/connaissances/:articleId/modifier" element={<ProtectedRoute permission="knowledge:manage" feature="governance"><KnowledgeArticleEditor /></ProtectedRoute>} />
            <Route path="achats/mon-materiel" element={<ProtectedRoute permission={["assets:view_own", "assets:view"]} feature="procurement"><ProcurementMine /></ProtectedRoute>} />
            {/* Documents et contrats (Ordre 18): reading needs records:view; creating also records:create (the server checks both). */}
            <Route path="documents" element={<ProtectedRoute permission="records:view" feature="records"><Records /></ProtectedRoute>} />
            <Route
              path="documents/nouveau"
              element={<ProtectedRoute permission="records:view" feature="records"><ProtectedRoute permission="records:create"><RecordFormPage /></ProtectedRoute></ProtectedRoute>}
            />
            <Route path="documents/obligations" element={<ProtectedRoute permission="records:view" feature="records"><RecordObligations /></ProtectedRoute>} />
            <Route path="documents/fichiers" element={<ProtectedRoute permission="records:view" feature="records"><RecordAllFiles /></ProtectedRoute>} />
            <Route path="documents/modeles" element={<ProtectedRoute permission="records:view" feature="records"><RecordTemplates /></ProtectedRoute>} />
            <Route path="documents/clauses" element={<ProtectedRoute permission="records:view" feature="records"><RecordClauses /></ProtectedRoute>} />
            <Route path="documents/clauses/:id" element={<ProtectedRoute permission="records:view" feature="records"><RecordClauseEditor /></ProtectedRoute>} />
            <Route path="documents/charte" element={<ProtectedRoute permission="records:manage_templates" feature="records"><RecordBrand /></ProtectedRoute>} />
            <Route path="documents/modeles/:id" element={<ProtectedRoute permission="records:view" feature="records"><RecordTemplateEditor /></ProtectedRoute>} />
            <Route
              path="documents/generer"
              element={<ProtectedRoute permission="records:view" feature="records"><ProtectedRoute permission="records:create"><RecordGenerate /></ProtectedRoute></ProtectedRoute>}
            />
            <Route path="documents/constitution" element={<ProtectedRoute permission="records:view" feature="records"><RecordConstitution /></ProtectedRoute>} />
            <Route path="documents/:id" element={<ProtectedRoute permission="records:view" feature="records"><RecordDocument /></ProtectedRoute>} />
            <Route
              path="missions"
              element={
                <ProtectedRoute permission={MISSION_READ_KEYS} feature="missions">
                  <Missions />
                </ProtectedRoute>
              }
            />
            <Route
              path="equipements"
              element={
                <ProtectedRoute permission={EQUIPMENT_READ_KEYS} feature="equipment">
                  <Equipment />
                </ProtectedRoute>
              }
            />
            <Route path="formations" element={<ProtectedRoute permission={CATALOG_READ_KEYS} feature="training"><Trainings /></ProtectedRoute>} />
            <Route path="formations/modules/nouveau"
              element={<ProtectedRoute permission={TRAINING_MANAGE_CATALOG} feature="training"><TrainingModuleForm /></ProtectedRoute>} />
            <Route path="formations/modules/:id"
              element={<ProtectedRoute permission={CATALOG_READ_KEYS} feature="training"><TrainingModuleForm /></ProtectedRoute>} />
            <Route path="formations/sessions/nouvelle"
              element={<ProtectedRoute permission={SESSION_CREATE_KEYS} feature="training"><TrainingSessionFormPage /></ProtectedRoute>} />
            <Route path="formations/sessions/:id"
              element={<ProtectedRoute permission={CATALOG_READ_KEYS} feature="training"><TrainingSession /></ProtectedRoute>} />
            <Route path="formations/programmes/:id"
              element={<ProtectedRoute permission={CATALOG_READ_KEYS} feature="training"><TrainingProgramEditor /></ProtectedRoute>} />
            <Route
              path="missions/nouvelle"
              element={
                <ProtectedRoute permission={MISSION_WRITE_KEYS} feature="missions">
                  <MissionFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="missions/procedures"
              element={
                <ProtectedRoute permission="procedures:manage" feature="procedures">
                  <Procedures />
                </ProtectedRoute>
              }
            />
            <Route
              path="missions/procedures/:id"
              element={
                <ProtectedRoute permission="procedures:manage" feature="procedures">
                  <ProcedureEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="missions/modeles-compte-rendu/:id"
              element={
                <ProtectedRoute permission="missions:manage_types" feature="missions">
                  <ProcedureEditor kind="report" />
                </ProtectedRoute>
              }
            />
            <Route
              path="missions/:id/compte-rendu"
              element={
                <ProtectedRoute permission={MISSION_READ_KEYS} feature="missions">
                  <MissionReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="missions/:id/procedures/:runId"
              element={
                <ProtectedRoute permission={MISSION_READ_KEYS} feature="procedures">
                  <ChecklistRun />
                </ProtectedRoute>
              }
            />
            <Route
              path="missions/:id"
              element={
                <ProtectedRoute permission={MISSION_READ_KEYS} feature="missions">
                  <MissionDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="collaborateurs"
              element={
                <ProtectedRoute permission="collaborators:view" feature="collaborators">
                  <Collaborators />
                </ProtectedRoute>
              }
            />
            <Route
              path="collaborateurs/nouveau"
              element={
                <ProtectedRoute permission="collaborators:create" feature="collaborators">
                  <CollaboratorFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="collaborateurs/import"
              element={
                <ProtectedRoute permission="collaborators:create" feature="collaborators">
                  <CollaboratorImportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="collaborateurs/organigramme"
              element={
                <ProtectedRoute permission="collaborators:view_org" feature="collaborators">
                  <Suspense fallback={<div className="py-16 text-center text-sm text-slate-500" data-testid="orgchart-page-loading">Chargement…</div>}><OrgChart /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="collaborateurs/organisation"
              element={
                <ProtectedRoute permission="collaborators:view" feature="collaborators">
                  <Organisation />
                </ProtectedRoute>
              }
            />
            <Route
              path="collaborateurs/organisation/:section"
              element={
                <ProtectedRoute permission="collaborators:view" feature="collaborators">
                  <Organisation />
                </ProtectedRoute>
              }
            />
            <Route
              path="collaborateurs/:id"
              element={
                <ProtectedRoute permission="collaborators:view" feature="collaborators">
                  <CollaboratorDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance"
              element={
                <ProtectedRoute feature="finance">
                  <Finance />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/depenses/nouvelle"
              element={
                <ProtectedRoute permission="finance:create_expense" feature="finance">
                  <FinanceExpenseFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/import"
              element={
                <ProtectedRoute permission="finance:create_expense" feature="finance">
                  <FinanceImportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/frais/nouvelle"
              element={
                <ProtectedRoute permission="finance:submit_claims" feature="finance">
                  <ClaimFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/frais/:id/modifier"
              element={
                <ProtectedRoute permission="finance:submit_claims" feature="finance">
                  <ClaimFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/depenses/:id/modifier"
              element={
                <ProtectedRoute permission="finance:edit_expense" feature="finance">
                  <FinanceExpenseFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="assistance"
              element={
                <ProtectedRoute permission={ASSIST_READ} feature="assist">
                  <Assistance />
                </ProtectedRoute>
              }
            />
            <Route
              path="assistance/:id"
              element={
                <ProtectedRoute permission={ASSIST_READ} feature="assist">
                  <AssistSession />
                </ProtectedRoute>
              }
            />
            <Route
              path="import"
              element={
                <ProtectedRoute permission="csv:import">
                  <CSVImport />
                </ProtectedRoute>
              }
            />
            <Route
              path="google-sheet"
              element={
                <ProtectedRoute permission="page:google_sheet">
                  <GoogleSheet />
                </ProtectedRoute>
              }
            />
            <Route path="apps" element={<Apps />} />
            <Route path="profile" element={<Profile />} />
            <Route path="copilote" element={<ProtectedRoute permission="assistant:use" feature="assistant"><CopilotPage /></ProtectedRoute>} />
            <Route
              path="users"
              element={
                <ProtectedRoute permission="users:view">
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="users/nouveau"
              element={
                <ProtectedRoute permission="users:create">
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="roles"
              element={
                <ProtectedRoute adminOnly>
                  <Roles />
                </ProtectedRoute>
              }
            />
            <Route
              path="activity"
              element={
                <ProtectedRoute permission="activity:view">
                  <Activity />
                </ProtectedRoute>
              }
            />
            <Route
              path="corbeille"
              element={
                <ProtectedRoute permission={TRASH_ACCESS}>
                  <Trash />
                </ProtectedRoute>
              }
            />
            <Route
              path="whatsapp"
              element={
                <ProtectedRoute permission={WHATSAPP_ACCESS}>
                  <WhatsApp />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute permission={SETTINGS_ACCESS}>
                  <Settings />
                </ProtectedRoute>
              }
            >
              <Route index element={<SettingsIndex />} />
              <Route path="tasks" element={<ProtectedRoute permission="settings:view"><SettingsTasks /></ProtectedRoute>} />
              <Route path="notifications" element={<ProtectedRoute permission="settings:view"><SettingsNotifications /></ProtectedRoute>} />
              <Route path="assist" element={<ProtectedRoute permission="settings:view" feature="assist"><SettingsAssist /></ProtectedRoute>} />
              <Route path="tickets" element={<ProtectedRoute permission="settings:view" feature="tickets"><SettingsTickets /></ProtectedRoute>} />
              <Route path="automation" element={<ProtectedRoute permission="automation:view" feature="automation"><SettingsAutomation /></ProtectedRoute>} />
              <Route path="agents" element={<ProtectedRoute permission="automation:manage_agents" feature="agent_access"><SettingsAgents /></ProtectedRoute>} />
              <Route path="billing" element={<ProtectedRoute permission="settings:view" feature="billing"><SettingsBilling /></ProtectedRoute>} />
              <Route path="general" element={<ProtectedRoute permission="settings:view"><SettingsGeneral /></ProtectedRoute>} />
              <Route path="deletion" element={<ProtectedRoute permission="settings:view"><SettingsDeletion /></ProtectedRoute>} />
              <Route path="records" element={<ProtectedRoute permission="settings:view" feature="records"><SettingsRecords /></ProtectedRoute>} />
              <Route path="procurement" element={<ProtectedRoute permission="settings:view" feature="procurement"><SettingsProcurement /></ProtectedRoute>} />
              <Route path="training-catalog" element={<ProtectedRoute permission={TRAINING_KEYS} feature="training"><SettingsTrainingCatalog /></ProtectedRoute>} />
              <Route
                path="whatsapp"
                element={
                  <ProtectedRoute permission={WHATSAPP_ACCESS}>
                    <WhatsApp embedded />
                  </ProtectedRoute>
                }
              />
              <Route
                path="google-sheet"
                element={
                  <ProtectedRoute permission="page:google_sheet">
                    <GoogleSheet embedded />
                  </ProtectedRoute>
                }
              />
              <Route
                path="system"
                element={
                  <ProtectedRoute permission="system:view">
                    <SettingsSystem />
                  </ProtectedRoute>
                }
              />
              <Route
                path="assistant"
                element={
                  <ProtectedRoute adminOnly feature="assistant">
                    <SettingsAssistant />
                  </ProtectedRoute>
                }
              />
              <Route
                path="assistant-usage"
                element={
                  <ProtectedRoute permission="assistant:view_usage" feature="assistant">
                    <SettingsAssistantUsage />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
